import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Building2, User, Calendar, CreditCard, FileText, 
  Phone, Mail, AlertTriangle, CheckCircle, Clock, Home,
  Banknote, Shield, MessageSquare, Wrench, History, 
  ChevronRight, ExternalLink, Download, Plus, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const statusConfig = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  ended: { label: 'Ended', color: 'bg-slate-100 text-slate-700', icon: History },
  notice: { label: 'Notice Given', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

export default function TenancyDetail() {
  const { tenancyId } = useParams();
  const navigate = useNavigate();
  const [tenancy, setTenancy] = useState(null);
  const [property, setProperty] = useState(null);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTenancyData();
  }, [tenancyId]);

  const loadTenancyData = async () => {
    try {
      // Load tenancy details
      const tenancyRes = await api.get(`/tenancies/${tenancyId}`);
      setTenancy(tenancyRes.data);
      
      // Load related property
      if (tenancyRes.data?.property_id) {
        try {
          const propRes = await api.get(`/properties/${tenancyRes.data.property_id}`);
          setProperty(propRes.data);
        } catch (e) {
          console.log('Could not load property');
        }
      }

      // Load payments
      try {
        const paymentsRes = await api.get(`/tenancies/${tenancyId}/payments`);
        setPayments(paymentsRes.data?.payments || []);
      } catch (e) {
        // Generate sample payments for demo
        setPayments(generateSamplePayments(tenancyRes.data));
      }

      // Load documents
      try {
        const docsRes = await api.get(`/tenancies/${tenancyId}/documents`);
        setDocuments(docsRes.data?.documents || []);
      } catch (e) {
        setDocuments(generateSampleDocuments());
      }

      // Load cases/issues
      try {
        const casesRes = await api.get(`/tenancies/${tenancyId}/cases`);
        setCases(casesRes.data?.cases || []);
      } catch (e) {
        setCases([]);
      }

      // Load history/timeline
      try {
        const historyRes = await api.get(`/tenancies/${tenancyId}/history`);
        setHistory(historyRes.data?.events || []);
      } catch (e) {
        setHistory(generateSampleHistory(tenancyRes.data));
      }

    } catch (error) {
      console.error('Error loading tenancy:', error);
      toast.error('Could not load tenancy details');
    } finally {
      setLoading(false);
    }
  };

  // Sample data generators for demo
  const generateSamplePayments = (t) => {
    if (!t) return [];
    const payments = [];
    const startDate = new Date(t.start_date);
    const now = new Date();
    let current = new Date(startDate);
    
    while (current <= now) {
      const isPaid = current < new Date(now.getFullYear(), now.getMonth(), 1);
      payments.push({
        id: `pay-${current.getTime()}`,
        due_date: current.toISOString(),
        amount: t.rent_amount,
        status: isPaid ? 'paid' : (current < now ? 'overdue' : 'pending'),
        paid_date: isPaid ? new Date(current.getTime() + 86400000).toISOString() : null,
        type: 'rent'
      });
      current.setMonth(current.getMonth() + 1);
    }
    return payments.reverse().slice(0, 12);
  };

  const generateSampleDocuments = () => [
    { id: 'doc-1', name: 'Tenancy Agreement', type: 'contract', created_at: new Date().toISOString(), status: 'signed' },
    { id: 'doc-2', name: 'Deposit Certificate', type: 'deposit', created_at: new Date().toISOString(), status: 'active' },
    { id: 'doc-3', name: 'Check-In Inventory', type: 'inventory', created_at: new Date().toISOString(), status: 'complete' },
    { id: 'doc-4', name: 'How to Rent Guide', type: 'legal', created_at: new Date().toISOString(), status: 'sent' },
  ];

  const generateSampleHistory = (t) => {
    if (!t) return [];
    return [
      { id: 'h1', date: t.start_date, type: 'tenancy_start', description: 'Tenancy commenced' },
      { id: 'h2', date: t.start_date, type: 'deposit', description: 'Deposit protected with DPS' },
      { id: 'h3', date: t.start_date, type: 'document', description: 'Tenancy agreement signed' },
      { id: 'h4', date: t.start_date, type: 'inventory', description: 'Check-in inventory completed' },
    ];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!tenancy) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Tenancy not found</h2>
          <Button onClick={() => navigate('/landlord/tenancies')} className="mt-4">
            View All Tenancies
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const tenant = tenancy.tenants?.[0] || tenancy.tenant || {};
  const StatusIcon = statusConfig[tenancy.status]?.icon || Clock;
  const paidPayments = payments.filter(p => p.status === 'paid');
  const overduePayments = payments.filter(p => p.status === 'overdue');
  const totalCollected = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="tenancy-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" data-testid="tenancy-title">Tenancy</h1>
              <Badge className={statusConfig[tenancy.status]?.color} data-testid="tenancy-status">
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig[tenancy.status]?.label || tenancy.status}
              </Badge>
            </div>
            <p className="text-slate-600 flex items-center gap-2 mt-1" data-testid="property-address">
              <Home className="w-4 h-4" />
              {property?.address?.line1 || tenancy.property_address || 'Property'}
              {property && (
                <Link to={`/landlord/properties/${property.id}`} className="text-primary hover:underline text-sm flex items-center gap-1" data-testid="view-property-link">
                  View Property <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="edit-tenancy-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" size="sm" data-testid="contact-tenant-btn">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Tenant
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
          <Card data-testid="stat-monthly-rent">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Monthly Rent</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(tenancy.rent_amount)}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-deposit">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Deposit Held</p>
              <p className="text-2xl font-bold">{formatCurrency(tenancy.deposit_amount)}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-collected">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Collected</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalCollected)}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-arrears">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Arrears</p>
              <p className={`text-2xl font-bold ${overduePayments.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {overduePayments.length > 0 ? formatCurrency(overduePayments.reduce((s,p) => s + p.amount, 0)) : '£0.00'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-4" data-testid="tenancy-tabs">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">Cases</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Tenant Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Tenant Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                      {(tenant.first_name?.[0] || 'T')}{(tenant.last_name?.[0] || '')}
                    </div>
                    <div>
                      <p className="font-semibold">{tenant.first_name} {tenant.last_name}</p>
                      <p className="text-sm text-slate-500">Lead Tenant</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      {tenant.email || 'No email'}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      {tenant.phone || 'No phone'}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </CardContent>
              </Card>

              {/* Tenancy Details */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Tenancy Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">Type</span>
                      <span className="font-medium">{tenancy.tenancy_type === 'assured_shorthold' ? 'AST' : tenancy.tenancy_type || 'AST'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">Start Date</span>
                      <span className="font-medium">{formatDate(tenancy.start_date)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">End Date</span>
                      <span className="font-medium">{formatDate(tenancy.end_date)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">Rent Due Day</span>
                      <span className="font-medium">{tenancy.rent_due_day || 1}st of month</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">Payment Method</span>
                      <span className="font-medium">{tenancy.rent_payment_method === 'direct_debit' ? 'Direct Debit' : 'Manual'}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-slate-500">Deposit Scheme</span>
                      <span className="font-medium">{tenancy.deposit_scheme || 'DPS'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alerts Section */}
            {overduePayments.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 flex items-center gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">Payment Overdue</p>
                    <p className="text-sm text-red-600">
                      {overduePayments.length} payment(s) totalling {formatCurrency(overduePayments.reduce((s,p) => s + p.amount, 0))} are overdue
                    </p>
                  </div>
                  <Button variant="destructive" size="sm">
                    View Arrears
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Property Link */}
            {property && (
              <Card className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/landlord/properties/${property.id}`)}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{property.title || property.address?.line1}</p>
                    <p className="text-sm text-slate-500">
                      {property.bedrooms} bed · {property.property_type} · {property.address?.city}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Payment History</CardTitle>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {payments.map((payment) => (
                    <div key={payment.id} className="flex items-center gap-4 p-3 border rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payment.status === 'paid' ? 'bg-green-100' :
                        payment.status === 'overdue' ? 'bg-red-100' : 'bg-amber-100'
                      }`}>
                        {payment.status === 'paid' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : payment.status === 'overdue' ? (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Monthly Rent</p>
                        <p className="text-sm text-slate-500">Due: {formatDate(payment.due_date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                        <Badge className={
                          payment.status === 'paid' ? 'bg-green-100 text-green-700' :
                          payment.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }>
                          {payment.status === 'paid' ? 'Paid' : payment.status === 'overdue' ? 'Overdue' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Documents</CardTitle>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-slate-50">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-slate-500">{formatDate(doc.created_at)}</p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Cases & Issues</CardTitle>
                  <Button size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Raise Case
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {cases.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No cases or issues for this tenancy</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cases.map((c) => (
                      <div key={c.id} className="flex items-center gap-4 p-3 border rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{c.title}</p>
                          <p className="text-sm text-slate-500">{c.category} · {formatDate(c.created_at)}</p>
                        </div>
                        <Badge>{c.status}</Badge>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Tenancy Timeline</CardTitle>
                <CardDescription>Complete history from start to present</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  <div className="space-y-4">
                    {history.map((event, i) => (
                      <div key={event.id} className="relative pl-10">
                        <div className="absolute left-2 w-5 h-5 rounded-full bg-white border-2 border-primary flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <p className="font-medium">{event.description}</p>
                          <p className="text-sm text-slate-500">{formatDate(event.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
